import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Alert } from 'react-bootstrap';
import { CheckCircle, XCircle, Clock, Home, Download, AlertTriangle, ArrowRight, RefreshCw, CreditCard } from 'lucide-react';
import { useLocation, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config.json';

const PaymentStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [applicationData, setApplicationData] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const [paymentInfo, setPaymentInfo] = useState({
    status: 'loading', // loading, SUCCESS, PENDING, FAILED, not_found, error
    amount: '₹200.00',
    applicationNo: 'N/A',
    transactionId: 'N/A',
    date: 'N/A',
    message: ''
  });

  const getStoredApplication = () => {
    try {
      const saved = sessionStorage.getItem('currentApplication');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    resolvePaymentStatus();
    window.scrollTo(0, 0);
  }, [location.search]);

  const resolvePaymentStatus = async () => {
    const queryParams = new URLSearchParams(window.location.search);
    const urlOrderId = queryParams.get('orderId') || queryParams.get('appNo');
    const urlMerchantOrderId = queryParams.get('merchantOrderId');
    const storedApp = getStoredApplication();

    const finalAppNo = urlOrderId || storedApp?.applicationID;
    const finalMerchantOrderId = urlMerchantOrderId || storedApp?.merchantOrderId;
    const finalClientId = storedApp?.clientID || 'MDUEDN';

    if (!finalAppNo && !finalMerchantOrderId) {
      setPaymentInfo(prev => ({ ...prev, status: 'not_found' }));
      return;
    }

    fetchOrderDetails(finalAppNo, finalMerchantOrderId, finalClientId);
  };

  const fetchOrderDetails = async (orderId, merchantOrderId, clientId) => {
    setIsVerifying(true);
    const apiBase = config?.api?.invokeURL;

    try {
      // 1. Try checking via PhonePe order status if merchantOrderId exists
      if (merchantOrderId) {
        try {
          const statusRes = await axios.get(`${apiBase}/transaction/phonepev2/order-status?merchantOrderId=${merchantOrderId}`, {
            headers: { 'x-api-key': config.api.key }
          });
          const rawStatus = statusRes.data?.status;

          let normalizedStatus = 'PENDING';
          if (rawStatus === 'COMPLETED' || rawStatus === 'SUCCESS') {
            normalizedStatus = 'SUCCESS';
          } else if (rawStatus === 'FAILED' || rawStatus === 'FAILURE' || rawStatus === 'CANCELLED') {
            normalizedStatus = 'FAILED';
          } else if (rawStatus === 'PENDING') {
            normalizedStatus = 'PENDING';
          }

          const saved = getStoredApplication() || {};
          saved.applicationStatus = normalizedStatus;
          sessionStorage.setItem('currentApplication', JSON.stringify(saved));
          setApplicationData(saved);

          setPaymentInfo({
            status: normalizedStatus,
            amount: '₹200.00',
            applicationNo: orderId || saved.applicationID || merchantOrderId,
            transactionId: merchantOrderId,
            date: new Date().toLocaleString(),
            message: statusRes.data?.message || ''
          });
          setIsVerifying(false);
          return;
        } catch (phonePeErr) {
          console.warn('PhonePe order-status check failed, falling back to transaction order lookup:', phonePeErr);
        }
      }

      // 2. Query application / order details from backend
      if (orderId) {
        const res = await axios.get(`${apiBase}/transaction/order?orderId=${orderId}&clientId=${clientId || 'MDUEDN'}`, {
          headers: { 'x-api-key': config.api.key }
        });

        if (res.data && res.data.length > 0) {
          const orderData = res.data[0];
          setApplicationData(orderData);
          const orderRefId = orderData.orderReferenceDetails?.orderId || orderData.transactionId || orderId;

          let paymentStatus = 'PENDING';
          if (orderData.orderReferenceDetails?.status === "COMPLETED" || orderData.txStatus === "SUCCESS" || orderData.applicationStatus === "Success") {
            paymentStatus = "SUCCESS";
          } else if (orderData.orderReferenceDetails?.status === "FAILED" || orderData.txStatus === "FAILED" || orderData.applicationStatus === "Failed") {
            paymentStatus = "FAILED";
          } else {
            paymentStatus = "PENDING";
          }

          orderData.applicationStatus = paymentStatus;
          sessionStorage.setItem('currentApplication', JSON.stringify(orderData));

          setPaymentInfo({
            status: paymentStatus,
            amount: '₹200.00',
            applicationNo: orderId,
            transactionId: orderRefId,
            date: new Date().toLocaleString(),
            message: ''
          });
          setIsVerifying(false);
          return;
        }
      }

      // If no valid status was confirmed
      const saved = getStoredApplication();
      if (saved) {
        setApplicationData(saved);
        setPaymentInfo({
          status: saved.applicationStatus === 'Success' ? 'SUCCESS' : 'PENDING',
          amount: '₹200.00',
          applicationNo: orderId || saved.applicationID,
          transactionId: merchantOrderId || saved.merchantOrderId || 'N/A',
          date: new Date().toLocaleString(),
          message: 'Status check pending'
        });
      } else {
        setPaymentInfo(prev => ({ ...prev, status: 'not_found' }));
      }
    } catch (err) {
      console.error('Error in payment status resolution:', err);
      const saved = getStoredApplication();
      if (saved) {
        setApplicationData(saved);
        setPaymentInfo({
          status: 'PENDING',
          amount: '₹200.00',
          applicationNo: orderId || saved.applicationID,
          transactionId: merchantOrderId || saved.merchantOrderId || 'N/A',
          date: new Date().toLocaleString(),
          message: 'Unable to verify status at this moment.'
        });
      } else {
        setPaymentInfo(prev => ({ ...prev, status: 'error' }));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  if (paymentInfo.status === 'loading') {
    return (
      <Container className="py-5 text-center" style={{ minHeight: '60vh' }}>
        <div className="p-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <h4 className="fw-bold text-dark">Verifying Payment Status</h4>
          <p className="text-muted small">Connecting to PhonePe gateway. Please do not close or refresh this window...</p>
        </div>
      </Container>
    );
  }

  if (paymentInfo.status === 'not_found') {
    return (
      <Container className="py-5" style={{ minHeight: '70vh' }}>
        <Row className="justify-content-center">
          <Col md={7} lg={5}>
            <Card className="border-0 shadow-soft text-center p-4 p-md-5 rounded-4 bg-white">
              <Card.Body>
                <div className="bg-light p-3 rounded-circle d-inline-block text-primary mb-3">
                  <CreditCard size={36} />
                </div>
                <h4 className="fw-bold text-dark">No Active Payment Session</h4>
                <p className="text-muted small mb-4">
                  We could not find an active transaction in your current browser session. You can look up your application anytime using your Application ID.
                </p>
                <Button as={NavLink} to="/application" variant="primary" className="rounded-pill px-4 py-2">
                  Track Application & Settle Fee
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  const isSuccess = paymentInfo.status === 'SUCCESS';
  const isPending = paymentInfo.status === 'PENDING';
  const isFailed = paymentInfo.status === 'FAILED' || paymentInfo.status === 'error';

  return (
    <Container className="py-5" style={{ minHeight: '80vh' }}>
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="border-0 shadow-soft text-center p-4 p-md-5 rounded-4 bg-white">
            <Card.Body className="p-0">

              {/* Status Header Icon */}
              <div className="mb-3">
                {isSuccess && (
                  <div className="text-success bg-success bg-opacity-10 d-inline-block p-4 rounded-circle">
                    <CheckCircle size={56} />
                  </div>
                )}
                {isPending && (
                  <div className="text-warning bg-warning bg-opacity-10 d-inline-block p-4 rounded-circle">
                    <Clock size={56} />
                  </div>
                )}
                {isFailed && (
                  <div className="text-danger bg-danger bg-opacity-10 d-inline-block p-4 rounded-circle">
                    <XCircle size={56} />
                  </div>
                )}
              </div>

              {/* Status Title & Message */}
              <h3 className="fw-bold mb-2 text-dark">
                {isSuccess && 'Payment Successful!'}
                {isPending && 'Payment Verification in Progress'}
                {isFailed && 'Payment Failed or Incomplete'}
              </h3>

              <p className="text-muted small px-3 mb-4">
                {isSuccess && 'Your transaction has been securely processed via PhonePe. Your job application is submitted.'}
                {isPending && 'We are confirming the final settlement with PhonePe and your bank. This may take up to 60 seconds.'}
                {isFailed && 'The transaction was declined or cancelled. If money was deducted, it will be refunded by your bank.'}
              </p>

              {/* Transaction Summary Table */}
              <div className="bg-light rounded-4 p-3 mb-4 border">
                <Table borderless responsive className="mb-0 text-start align-middle">
                  <tbody>
                    <tr>
                      <td className="text-muted py-2 ps-2 small">Application Number:</td>
                      <td className="fw-bold text-dark text-end pe-2">{paymentInfo.applicationNo}</td>
                    </tr>
                    {paymentInfo.transactionId && paymentInfo.transactionId !== 'N/A' && (
                      <tr>
                        <td className="text-muted py-2 ps-2 small">Transaction ID:</td>
                        <td className="text-dark font-monospace text-end pe-2 small">{paymentInfo.transactionId}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="text-muted py-2 ps-2 small">Amount:</td>
                      <td className={`fw-bold text-end pe-2 ${isSuccess ? 'text-success' : isPending ? 'text-warning' : 'text-danger'}`}>
                        {paymentInfo.amount}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted py-2 ps-2 small">Status:</td>
                      <td className="text-end pe-2">
                        <span className={`badge ${isSuccess ? 'bg-success' : isPending ? 'bg-warning text-dark' : 'bg-danger'}`}>
                          {paymentInfo.status}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted py-2 ps-2 small">Timestamp:</td>
                      <td className="text-muted text-end pe-2 small">{paymentInfo.date}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {/* Informative Alerts */}
              {isPending && (
                <Alert variant="warning" className="text-start small rounded-3 mb-4">
                  <div className="d-flex align-items-center gap-2">
                    <Clock size={18} className="flex-shrink-0" />
                    <span>Please click below to check updated status or verify from the Track Application portal.</span>
                  </div>
                </Alert>
              )}

              {isFailed && (
                <Alert variant="danger" className="text-start small rounded-3 mb-4">
                  <div className="d-flex align-items-center gap-2">
                    <AlertTriangle size={20} className="flex-shrink-0" />
                    <span>If your bank account was debited, PhonePe will automatically refund the amount within 3-5 business days.</span>
                  </div>
                </Alert>
              )}

              {/* Action Buttons */}
              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                {isSuccess && (
                  <Button
                    variant="success"
                    className="rounded-pill px-4 py-2 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => window.print()}
                  >
                    <Download size={16} /> Print Receipt
                  </Button>
                )}

                {isPending && (
                  <Button
                    variant="primary"
                    className="rounded-pill px-4 py-2 d-flex align-items-center justify-content-center gap-2"
                    onClick={resolvePaymentStatus}
                    disabled={isVerifying}
                  >
                    {isVerifying ? <Spinner size="sm" animation="border" /> : <RefreshCw size={16} />}
                    Refresh Status
                  </Button>
                )}

                {isFailed && (
                  <Button
                    variant="primary"
                    as={NavLink}
                    to="/application"
                    className="rounded-pill px-4 py-2 d-flex align-items-center justify-content-center gap-2"
                  >
                    <ArrowRight size={16} /> Re-attempt Payment
                  </Button>
                )}

                <Button
                  variant="outline-secondary"
                  as={NavLink}
                  to="/application"
                  className="rounded-pill px-4 py-2 d-flex align-items-center justify-content-center gap-2"
                >
                  <Home size={16} /> Back to Portal
                </Button>
              </div>

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PaymentStatus;